import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { soundchartsEndpoints } from "../../repositories/soundcharts";
import { playlistsApi } from "../../repositories/playlists";
import { pitchesApi } from "../../repositories/pitches";
import { formatDisplayDate, mmddyyToISO } from "../../DashboardPage/utils/dates";
import { getFirstMonthAudience, getFirstMonthWindow } from "../../utils/audienceSeries";
import CSVDownload from "../../components/ui/CSVDownload";
import { countryCodes } from "./Data/dropdown";
import Select from "react-select"; // Add at the top
import { ArrowDownTrayIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import AnalyticsExportModal from "../../components/modal/AnalyticsExportModal";
import RadioSpinsExportModal from "../../components/modal/RadioSpinsExportModal";
import HistoricalTrendModal from "../../components/modal/HistoricalTrendModal"; // Import the new modal
import { useUser } from "../../hooks/useUser";
import { TrendingUp, TrendingDown, BarChart3 } from "lucide-react";
import Pagination from "../../components/common/Pagination";
import { playlistCountLabel } from "../../components/playlists/playlistCountLabel";
import { PlaylistFreshness } from "../../components/playlists/PlaylistFreshness";
import {
  CurrentPositionCell,
  EntryDateCell,
  ExitDateCell,
} from "../../components/playlists/PlaylistDateCells";
import {
  StaleDividerRow,
  staleArtworkClass,
  staleLast,
  startsStaleGroup,
} from "../../components/playlists/stalePlacements";
import type { PlaylistMeta, PlaylistRowHonesty } from "../../repositories/playlists";
import PageHeader from "../../components/ui/PageHeader";
import { HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import StatCard from "../../components/ui/StatCard";
import { formSecondaryButtonClass } from "../../components/ui/formStyles";
import Button from "../../components/ui/Button";
import { buttonVariants } from "../../components/ui/buttonVariants";
import {
  Skeleton,
  SkeletonFields,
  SkeletonTableRows,
  SkeletonText,
} from "../../components/ui/skeleton";
import DataTable, {
  DataTableColumn,
  dataTableThClass,
  dataTableTheadRowClass,
} from "../../components/ui/DataTable";
import { ValueSetter } from "date-fns/parse/_lib/Setter";

/*
 * Shared button / pill / toggle styles for this page. The palette is
 * primary-700 + black + white only - the teal accent this page used to carry
 * is not part of the design system.
 */
const primaryButtonClass = buttonVariants({ variant: "primary" });

/** Segmented pill: active is a primary-700 fill, inactive a neutral surface. */
const pillClass = (active: boolean) =>
  `px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus-visible:border-primary-700 ${
    active
      ? "bg-primary-700 text-white"
      : "bg-neutral-100 text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
  }`;

const inputClass =
  "px-3 py-2 text-sm bg-white border rounded-lg border-neutral-300 text-black placeholder-neutral-400 focus:outline-none focus:border-primary-500";

interface Pitch {
  id: number;
  userId: number;
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  configuration: string;
  releaseUPC: string;
  trackISRC: string;
  featuredArtists: string;
  featuredArtistURI: string;
  altTitles: string;
  shortDescription: string;
  longDescription: string;
  lyrics: string;
  songDescription: string;
  mainGenre: string;
  songMood: string;
  language: string;
  linkToListen: string;
  promoPhotos: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  AmazonInfo: any;
  SpotifyInfo: any;
  DeezerInfo: {
    id: number;
    priority: string;
    editorialTeam: string;
    subgenre: string;
    pitchId: number;
    createdAt: string;
    updatedAt: string;
  } | null;
  TidalInfo: {
    id: number;
    subgenre: string;
    artistPageLink: string;
    country: string;
    labelDistributor: string;
    socialHandles: string;
    pitchId: number;
    createdAt: string;
    updatedAt: string;
  } | null;
  AudiomackInfo: any;
  SoundCloudInfo: any;
  User: {
    id: number;
    fullName: string;
    email: string;
    labelId: number;
    Label: { id: number; name: string; type: string };
  };
  company: string;
}

interface Playlist extends PlaylistRowHonesty {
  playlist: {
    uuid: string;
    name: string;
    imageUrl: string;
    platform: string;
    owner?: { name: string };
    type?: string;
    countryCode?: string;
    latestSubscriberCount?: number;
    latestTrackCount?: number;
  };
  peakPosition: number;
  position: number;
  entryDate: string;
  exitDate?: string;
  positionDate?: string;
  peakPositionDate?: string;
  isEditorial: boolean;
  playlistType: "editorial" | "algorithmic" | "algotorial";
}

const formatErrorMessage = (error: any): string => {
  if (typeof error === "string") return error;
  if (error?.response) {
    const { status, statusText, data } = error.response;
    switch (status) {
      case 400:
        return (
          data?.message || "Invalid request. Check your data and try again."
        );
      case 401:
        return "Unauthorized. Please log in again.";
      case 403:
        return "Permission denied.";
      case 404:
        return "Resource not found. Check the ID and try again.";
      case 429:
        return "Too many requests. Wait and try again.";
      case 500:
        return "Server error. Try again later.";
      case 502:
      case 503:
      case 504:
        return "Service unavailable. Try again in a few minutes.";
      default:
        return data?.message || `Failed with status ${status}: ${statusText}`;
    }
  }
  if (error?.code) {
    switch (error.code) {
      case "NETWORK_ERROR":
      case "ERR_NETWORK":
        return "Network failed. Check your internet.";
      case "TIMEOUT":
      case "ERR_TIMEOUT":
        return "Request timed out. Try again.";
      case "CANCELED":
      case "ERR_CANCELED":
        return "Request canceled.";
      default:
        return error.message || "Unexpected error. Try again.";
    }
  }
  return error?.message || "Unexpected error. Try again.";
};
const cleanISRC = (isrc: string): string => {
  return isrc.replace(/[^a-zA-Z0-9]/g, '');
};

// An ISRC is 12 characters: 2-letter country code, 3-char alphanumeric registrant,
// 2-digit year, 5-digit designation. Pitches created with a malformed ISRC used to
// reach SoundCharts anyway, where the request 404s or stalls into the 10s axios
// timeout - surfacing "Request timed out" instead of telling the user their ISRC is
// wrong. Validating here keeps that bad input from ever leaving the browser.
const ISRC_PATTERN = /^[A-Z]{2}[A-Z0-9]{3}\d{2}\d{5}$/;
const isValidISRC = (isrc: string): boolean => ISRC_PATTERN.test(isrc.toUpperCase());
// YouTube's first-month number adds up every video linked to the song, unlike Spotify's.
const YOUTUBE_AUDIENCE_TOOLTIP =
  "Total views across the YouTube videos linked to this song, such as the music video, lyric video, visualizer and official audio.";
const Analytics = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || "past-submissions";
  /*
   * The list page this was opened from passes its position through so this
   * breadcrumb can hand it back. Analytics sends the full path (?returnTo=
   * "/analytics?tab=release&page=3"); Past Submissions sends just ?page=7.
   * Without it the user lands on page 1 of a list they were seven pages into.
   */
  const returnTo = searchParams.get("returnTo") || "/analytics";
  const returnPage = searchParams.get("page");
  const backTo =
    source === "analytics"
      ? returnTo
      : `/past-submissions${returnPage ? `?page=${encodeURIComponent(returnPage)}` : ""}`;
  const { user, fetchProfile } = useUser();
  const [pitch, setPitch] = useState<Pitch | null>(null);
  const [loading, setLoading] = useState(false);
  const [uuid, setUuid] = useState<string | null>(null);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Non-fatal: the pitch loaded fine but its track couldn't be resolved in SoundCharts
  // (bad/unknown ISRC). Shown as a notice above the still-rendered track details rather
  // than replacing the whole page the way `error` does.
  const [trackLookupNotice, setTrackLookupNotice] = useState<string | null>(null);
  // Distinct from `loading`, which also covers per-tab playlist fetches. Only the initial
  // pitch/track load may take over the whole page; switching DSP or country must leave the
  // track details and filter controls on screen and confine the spinner to the table.
  const [initialLoading, setInitialLoading] = useState(true);
  // Add these new state variables for popularity scores
  const [songPopularity, setSongPopularity] = useState<number | null>(null);
  const [artistPopularity, setArtistPopularity] = useState<number | null>(null);
  const [popularityLoading, setPopularityLoading] = useState(false);

  // Add new state variables for the modal
  const [showTrendModal, setShowTrendModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [trendData, setTrendData] = useState<any[]>([]);
  const [trendDataType, setTrendDataType] = useState<'artist' | 'song'>('song');

  const [filterType, setFilterType] = useState<
    "editorial" | "algorithmic" | "algotorial"
  >("editorial");
  const [activePlatform, setActivePlatform] = useState<string>("");
  const [availablePlatforms, setAvailablePlatforms] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  // A display page size, not a fetch size. The backend fetches the complete set; this is only
  // how many deduplicated rows we show at a time.
  const [itemsPerPage] = useState(10);
  // Count of real placements after deduplication, straight from the backend.
  const [playlistsTotal, setPlaylistsTotal] = useState(0);
  // True when the backend's page cap stopped its walk early, so the set below is incomplete and
  // the user has to be told rather than shown a silently truncated list.
  const [playlistsCapped, setPlaylistsCapped] = useState(false);
  // The market the result is scoped to. Song scope never has one applied for it, so this is only
  // ever the country picked above — but it is read from the response rather than from that state so
  // the label cannot disagree with the data it sits over.
  const [playlistsCountry, setPlaylistsCountry] = useState<string | null>(null);
  // Rows to page through. Usually the same as playlistsTotal, but stored data lists stale
  // placements without counting them, so paging by the count would hide the last of them.
  const [playlistsPageTotal, setPlaylistsPageTotal] = useState(0);
  // Where the data came from and how fresh it is; drives the "Data as of" line.
  const [playlistsMeta, setPlaylistsMeta] = useState<PlaylistMeta | null>(null);
  const [refreshingPlaylists, setRefreshingPlaylists] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  // Set before the read that follows a refresh, so that one read skips the browser's cache.
  const bustNextPlaylistReadRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentOnly, setCurrentOnly] = useState<1 | 0>(1);
  const [totalAudience, setTotalAudience] = useState<number | null>(0);
  const [audienceByPlatform, setAudienceByPlatform] = useState<{ [key: string]: number | null }>({});
  const [isReleaseDateOlderThan30Days, setIsReleaseDateOlderThan30Days] = useState<boolean>(false);
  const [audienceLoading, setAudienceLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<{ value: string; label: string } | null>(null);
  // siriusxm is deliberately absent: SoundCharts has no playlist endpoint for it and answered every
  // request with HTTP 400, which this page swallowed and rendered as an empty result. The backend
  // rejects it outright now. SiriusXM remains a DSP in the pitch form, which is a separate feature.
  const platformDisplayNames: { [key: string]: string } = {
    spotify: "Spotify",
    amazon: "Amazon Music",
    deezer: "Deezer",
    tidal: "TIDAL",
    "apple-music": "Apple Music",
    youtube: "YouTube",
    audiomack: "Audiomack",
    "sound-cloud": "SoundCloud",
  };

  // Reverse mapping: DSP display name -> platform key. A label may have SiriusXM among its DSPs;
  // it maps to nothing here because there is no SiriusXM playlist platform to select.
  const dspToPlatformKey: { [key: string]: string } = {
    "Spotify": "spotify",
    "Amazon Music": "amazon",
    "Deezer": "deezer",
    "Deezer for Creators": "deezer",
    "TIDAL": "tidal",
    "Audiomack": "audiomack",
    "Apple Music": "apple-music",
    "SoundCloud": "sound-cloud",
    "YouTube": "youtube"
  };
  const [showExportModal, setShowExportModal] = useState(false);
  // Add new state to store full responses
  const [songPopResponse, setSongPopResponse] = useState<any>(null);
  const [artistPopResponse, setArtistPopResponse] = useState<any>(null);
  const [artistUuid, setArtistUuid] = useState<string | null>(null);

  //radio-spins
  const [radioSpins, setRadioSpins] = useState<any[]>([]);
  const [radioSpinsLoading, setRadioSpinsLoading] = useState(false);
  const [radioSpinsError, setRadioSpinsError] = useState<string | null>(null);
  //pagination 
  const [radioSpinsOffset, setRadioSpinsOffset] = useState(0);
  const [radioSpinsLimit] = useState(10);

  const [radioSpinsHasMore, setRadioSpinsHasMore] = useState(true);
  const [radioSpinsTotal, setRadioSpinsTotal] = useState(0);
  const [radioSpinsPage, setRadioSpinsPage] = useState(1);
  const [radioSpinsCountry, setRadioSpinsCountry] = useState<string>("US");
  const [showRadioSpinsExportModal, setShowRadioSpinsExportModal] = useState(false);

  const getAudience = async (uuid: string, activePlatform: string, startDate: string, endDate: string) => {
    try {
      const res = await soundchartsEndpoints.getAudienceByUUIDAndPlatform(uuid, activePlatform, startDate, endDate);
      return res;
    } catch (error: any) {
      return error.response;
    }
  }

  // Map internal platform key to Soundcharts API platform code
  const getApiPlatformCode = (platform: string): string => {
    if (platform === "sound-cloud") return "soundcloud";
    return platform;
  };

  // Map filter type to the correct API type based on platform
  const getApiPlaylistType = (
    type: "editorial" | "algorithmic" | "algotorial" | "curators_listeners",
    platform: string
  ): "editorial" | "algorithmic" | "algotorial" | "curators_listeners" => {
    if (type === "editorial" && platform === "sound-cloud") {
      return "curators_listeners";
    }
    return type;
  };

  // Fetches one display page from the Cadence backend, which has already walked SoundCharts'
  // page.next to the end and deduplicated across the whole set. `meta.totalPlaylists` is
  // therefore a count of real placements and drives pagination directly - unlike SoundCharts'
  // page.total, which counts raw rows and overstates platforms that repeat a playlist per market.
  const fetchPlaylistsForCurrentTab = useCallback(
    async (
      songUuid: string,
      platform: string,
      type: "editorial" | "algorithmic" | "algotorial" | "curators_listeners",
      page: number = 1,
      currentOnly: 0 | 1,
      limit: number = itemsPerPage,
      countryCode: string = "",
    ) => {
      try {
        const apiType = getApiPlaylistType(type, platform);
        const apiPlatform = getApiPlatformCode(platform);

        const bustBrowserCache = bustNextPlaylistReadRef.current;
        bustNextPlaylistReadRef.current = false;

        const res = await playlistsApi.getPlaylists(
          "song",
          songUuid,
          apiPlatform,
          { type: apiType, currentOnly, countryCode, page, limit, bustBrowserCache }
        );

        const items = res?.data?.data || [];
        const meta = res?.data?.meta;

        setPlaylistsTotal(meta?.totalPlaylists ?? items.length);
        setPlaylistsPageTotal(res?.data?.pagination?.totalItems ?? meta?.totalPlaylists ?? items.length);
        setPlaylistsCapped(Boolean(meta?.capped));
        setPlaylistsCountry(meta?.countryCode ?? null);
        setPlaylistsMeta(meta ?? null);

        return items.map((item: any) => ({
          ...item,
          isEditorial: type === "editorial",
          playlistType: type,
        }));
      } catch (error) {
        console.error(
          `❌ Error fetching ${type} playlists for ${platform}:`,
          error
        );
        setPlaylistsTotal(0);
        setPlaylistsPageTotal(0);
        setPlaylistsCapped(false);
        setPlaylistsMeta(null);
        return [];
      }
    },
    [itemsPerPage]
  );

  // The only thing fetchInitialData needs from `user` is which DSP tab to open first.
  // Held in a ref so that callback stays stable across `user` identity changes.
  const preferredAnalyticsDSP =
    user?.Label?.availableAnalyticsDSPs?.[0] ?? null;
  const preferredAnalyticsDSPRef = useRef<string | null>(preferredAnalyticsDSP);
  preferredAnalyticsDSPRef.current = preferredAnalyticsDSP;

  const getFirstAnalyticsDSPs = useCallback((): string | null => {
    if (user?.Label?.availableAnalyticsDSPs && user.Label.availableAnalyticsDSPs.length > 0) {
      for (const dsp of user.Label.availableAnalyticsDSPs) {
        const platformKey = dspToPlatformKey[dsp];
        if (platformKey && availablePlatforms.includes(platformKey)) {
          return platformKey;
        }
      }
    }
    return null;
  }, [user?.Label?.availableAnalyticsDSPs, availablePlatforms]);


  const fetchDataForCurrentTab = useCallback(async () => {
    if (!uuid || !activePlatform || !filterType) return;

    try {
      setLoading(true);
      setError(null);

      const fetchedPlaylists = await fetchPlaylistsForCurrentTab(
        uuid,
        activePlatform,
        filterType, // Pass the current filter type to API
        1,
        currentOnly,
        itemsPerPage,
        selectedCountry?.value || "",
      );

      setPlaylists(fetchedPlaylists);
      setCurrentPage(1);
    } catch (error: any) {
      console.error("❌ Error fetching data:", error);
      setError(formatErrorMessage(error));
      setPlaylists([]);
    } finally {
      setLoading(false);
    }
  }, [uuid, activePlatform, filterType, currentOnly, itemsPerPage, selectedCountry, fetchPlaylistsForCurrentTab]);

  // Go to a specific playlists page. The backend pages the deduplicated set, so the page number
  // goes over the wire directly rather than being converted to a SoundCharts offset.
  const goToPlaylistsPage = useCallback(
    async (page: number) => {
      if (!uuid || !activePlatform || !filterType || loading || page < 1) return;
      const totalPages = Math.max(1, Math.ceil(playlistsPageTotal / itemsPerPage));
      if (page === currentPage || page > totalPages) return;

      try {
        setLoading(true);

        const fetchedPlaylists = await fetchPlaylistsForCurrentTab(
          uuid,
          activePlatform,
          filterType,
          page,
          currentOnly,
          itemsPerPage,
          selectedCountry?.value || "",
        );

        setPlaylists(fetchedPlaylists);
        setCurrentPage(page);
      } catch (error: any) {
        console.error("❌ Error loading playlists page:", error);
        setError(formatErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [
      uuid,
      activePlatform,
      filterType,
      currentOnly,
      itemsPerPage,
      loading,
      currentPage,
      playlistsPageTotal,
      selectedCountry,
      fetchPlaylistsForCurrentTab,
    ]
  );

  // A refresh message belongs to the target it was about; moving to another platform, type or
  // tab starts clean.
  useEffect(() => {
    setRefreshMessage(null);
  }, [activePlatform, filterType, currentOnly]);

  /**
   * Syncs this record on this platform from SoundCharts, then reads the table again.
   *
   * It cannot beat SoundCharts' own crawl: if they have not recrawled since the last sync, the
   * rows come back the same. The backend allows one refresh per record and platform per five
   * minutes, and none while the record's history is still loading.
   */
  const handleRefreshPlaylists = useCallback(async () => {
    if (!uuid || !activePlatform || refreshingPlaylists) return;

    setRefreshingPlaylists(true);
    setRefreshMessage(null);

    try {
      const res = await playlistsApi.refreshPlaylists(
        "song",
        uuid,
        getApiPlatformCode(activePlatform)
      );
      if (res?.data?.message) setRefreshMessage(res.data.message);

      bustNextPlaylistReadRef.current = true;
      await fetchDataForCurrentTab();
    } catch (error: any) {
      setRefreshMessage(
        error?.response?.data?.message || "Could not refresh. Try again shortly."
      );
    } finally {
      setRefreshingPlaylists(false);
    }
  }, [uuid, activePlatform, refreshingPlaylists, fetchDataForCurrentTab]);

  const fetchInitialData = useCallback(async () => {
    if (!id) {
      setError("No pitch ID provided. Select a valid pitch.");
      setPitch(null);
      setPlaylists([]);
      setInitialLoading(false);
      return;
    }

    const pitchId = parseInt(id);
    if (isNaN(pitchId) || pitchId <= 0) {
      setError("Invalid pitch ID format. Provide a valid numeric ID.");
      setPitch(null);
      setPlaylists([]);
      setInitialLoading(false);
      return;
    }

    try {
      setLoading(true);
      setInitialLoading(true);
      setError(null);
      setTrackLookupNotice(null);

      const pitchResponse = await pitchesApi.getOne(pitchId);
      if (!pitchResponse?.data?.data)
        throw new Error("Pitch not found or access denied.");

      const pitchData = pitchResponse.data.data;
      setPitch(pitchData);

      if (!pitchData.trackISRC) {
        console.warn("⚠️ No ISRC found for this pitch");
        setPlaylists([]);
        setAvailablePlatforms([]);
        return;
      }

      const cleanedISRC = cleanISRC(pitchData.trackISRC);

      // Reject malformed ISRCs before hitting SoundCharts. Without this the request goes
      // out anyway and the user sees a timeout/"service unavailable" error for what is
      // really a data-entry mistake they can fix on the pitch.
      if (!isValidISRC(cleanedISRC)) {
        console.warn("⚠️ Invalid ISRC format on pitch:", pitchData.trackISRC);
        setTrackLookupNotice(
          `The ISRC on this pitch ("${pitchData.trackISRC}") isn't a valid ISRC, so streaming analytics can't be loaded. ` +
            `An ISRC is 12 characters, for example USRC17607839. Edit the pitch to correct it.`
        );
        setPlaylists([]);
        setAvailablePlatforms([]);
        return;
      }

      // A lookup failure here is about the track, not the pitch - keep it out of the
      // page-level `error` so the track details above stay on screen.
      let songRes;
      try {
        songRes = await soundchartsEndpoints.getSongByISRC(cleanedISRC);
      } catch (lookupError: any) {
        console.warn("⚠️ Soundcharts lookup failed for ISRC:", cleanedISRC, lookupError);
        const status = lookupError?.response?.status;
        setTrackLookupNotice(
          status === 404
            ? `No track matching ISRC ${cleanedISRC} was found in SoundCharts. Confirm the ISRC on this pitch is correct and that the release has been delivered.`
            : `Streaming analytics couldn't be loaded right now (${formatErrorMessage(
                lookupError
              )}). The pitch details below are unaffected.`
        );
        setPlaylists([]);
        setAvailablePlatforms([]);
        return;
      }

      if (!songRes?.data?.object?.uuid) {
        console.warn(
          "⚠️ Song not found in Soundcharts for ISRC:",
          pitchData.trackISRC
        );
        setTrackLookupNotice(
          `No track matching ISRC ${cleanedISRC} was found in SoundCharts. Confirm the ISRC on this pitch is correct and that the release has been delivered.`
        );
        setPlaylists([]);
        setAvailablePlatforms([]);
        return;
      }

      const fetchedUuid = songRes.data.object.uuid;
      const artistUuid = songRes.data.object?.artists?.[0]?.uuid;


      setUuid(fetchedUuid);
      setArtistUuid(artistUuid); // Add this line

      // Fetch popularity scores
      if (fetchedUuid || artistUuid) {
        setPopularityLoading(true);
        try {
          // Fetch song popularity
          if (fetchedUuid) {
            const songPopResponse = await soundchartsEndpoints.getPopularityBySong(fetchedUuid, "spotify");
            setSongPopResponse(songPopResponse); // Store full response
            let latestValue = songPopResponse?.data?.items?.[0]
            let array: number[] = []
            if (latestValue?.plots) {
              latestValue?.plots.forEach((plot: any) => {
                array.push(plot.value);
                setSongPopularity(Math.max(...array));

              })

            } else {
              setSongPopularity(latestValue?.value);
            }

          }

          // Fetch artist popularity
          if (artistUuid) {
            const today = new Date();
            const previousDate = new Date(today);
            previousDate.setDate(today.getDate() - 1)
            const previousFormattedDate = previousDate.toISOString().split('T')[0];

            const artistPopResponse = await soundchartsEndpoints.getPopularityByArtist(artistUuid, "spotify", `startDate=${previousFormattedDate}`);
            setArtistPopResponse(artistPopResponse); // Store full response
            let latestValue = artistPopResponse?.data?.items?.[0]
            let array: number[] = []
            if (latestValue?.plots) {
              latestValue?.plots.forEach((plot: any) => {
                array.push(plot.value);
                setArtistPopularity(Math.max(...array));

              })

            } else {
              setArtistPopularity(latestValue?.value);
            }
          }
        } catch (error) {
          console.error("Failed to fetch popularity scores:", error);
        } finally {
          setPopularityLoading(false);
        }
      }

      const audiencePlatforms = ["spotify", "youtube", "soundcloud", "audiomack"];
      // Soundcharts' release date is read on every page load: when the pitch was created the
      // song usually wasn't out yet, so Soundcharts had no date for it then.
      const firstMonth = getFirstMonthWindow(songRes.data.object.releaseDate, pitchData.releaseDate);
      const today = mmddyyToISO(formatDisplayDate(new Date().toString()));
      const firstMonthComplete = !!firstMonth && today >= firstMonth.endDate;
      setIsReleaseDateOlderThan30Days(firstMonthComplete);
      setAudienceLoading(true);
      if (firstMonth && firstMonthComplete) {
        const { startDate, endDate } = firstMonth;
        Promise.all(
          audiencePlatforms.map(platform =>
            getAudience(fetchedUuid, platform, startDate, endDate)
              .then(res => {
                if (res?.status === 404) {
                  return { platform, total: null };
                }
                return { platform, total: getFirstMonthAudience(res?.data?.items, platform, endDate) };
              })
              .catch(() => ({ platform, total: null }))
          )).then(results => {
            const audienceObj: { [key: string]: number | null } = {};
            results.forEach(({ platform, total }) => {
              audienceObj[platform] = total;
            });
            setAudienceByPlatform(audienceObj);

            // Only platforms that have data count towards the total; with none at all the
            // total is "No data" rather than a 0 that reads as zero streams.
            const withData = Object.values(audienceObj).filter(
              (total): total is number => typeof total === "number" && !isNaN(total)
            );
            setTotalAudience(withData.length > 0 ? withData.reduce((sum, total) => sum + total, 0) : null);
          });
      }
      setTimeout(() => setAudienceLoading(false), 2000); // show loading for at least 2 seconds
      // Which DSPs this pitch went to, as playlist platform keys. SiriusXMInfo has no entry: a
      // pitch can go to SiriusXM, but SoundCharts has no playlist data for it, so it must not
      // become a selectable platform here.
      const platformMapping: { [key: string]: string } = {
        SpotifyInfo: "spotify",
        AmazonInfo: "amazon",
        DeezerInfo: "deezer",
        TidalInfo: "tidal",
        AudiomackInfo: "audiomack",
        AppleInfo: "apple-music",
        SoundCloudInfo: "sound-cloud",
        YoutubeInfo: "youtube"
      };

      const platforms = Object.entries(platformMapping)
        .filter(([pitchKey]) => (pitchData as any)[pitchKey])
        .map(([, platformName]) => platformName);

      setAvailablePlatforms(platforms);
      if (platforms.length === 0) {
        setPlaylists([]);
        return;
      }

      // Open on the label's first configured DSP when the profile has already loaded.
      // If it hasn't, fall back to the first available platform now and let the effect
      // below correct the tab once the profile arrives - reading the preference through
      // a ref keeps it out of this callback's dependencies, so a profile load can never
      // re-trigger the pitch/ISRC/popularity requests above.
      const preferredDSP = preferredAnalyticsDSPRef.current;
      const preferredPlatform = preferredDSP ? dspToPlatformKey[preferredDSP] : undefined;
      setActivePlatform(preferredPlatform || platforms[0]);
    } catch (error: any) {
      console.error("❌ Error fetching initial data:", error);
      setError(formatErrorMessage(error));
      setPitch(null);
      setPlaylists([]);
      setAvailablePlatforms([]);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
    // Deliberately depends only on `id`. `useUser` keeps `user` in local state, so
    // fetchProfile() flipping it from null -> object used to change this callback's
    // identity and re-run the mount effect below, refetching the pitch, the ISRC lookup
    // and the popularity calls a second time. The DSP preference is read via ref instead.
  }, [id]);



  // -----Fetch Radio Spins Function--------
  const fetchRadioSpins = async (
    fetchedUuid: string,
    offsetVal: number = 0,
    limitVal: number = 10,
    countryCode: string = ""
  ) => {
    setRadioSpinsLoading(true);
    setRadioSpinsError(null);

    try {
      let query = `offset=${offsetVal}&limit=${limitVal}`;
      if (countryCode) {
        query += `&countryCode=${countryCode}`;
      }
      const response = await soundchartsEndpoints.getRadioSpins(fetchedUuid, query);
      const items = response?.data?.items || [];
      const pageInfo = response?.data?.page || {};

      setRadioSpins(items);
      setRadioSpinsTotal(pageInfo.total ?? 0);
      setRadioSpinsHasMore(pageInfo.next !== null && pageInfo.next !== undefined);
      setRadioSpinsOffset(offsetVal);
    } catch (error) {
      setRadioSpinsError("Failed to fetch radio spins");
      setRadioSpins([]);
      setRadioSpinsHasMore(false);
    } finally {
      setRadioSpinsLoading(false);
    }
  };

  // Go to a specific radio spins page
  const goToRadioSpinsPage = (page: number) => {
    if (!uuid || radioSpinsLoading || page === radioSpinsPage || page < 1) return;
    const newOffset = (page - 1) * radioSpinsLimit;
    setRadioSpinsPage(page);
    fetchRadioSpins(uuid, newOffset, radioSpinsLimit, radioSpinsCountry);
  };

  // Effect for fetching radio spins
  useEffect(() => {
    if (source === "analytics" && uuid) {
      setRadioSpinsOffset(0);
      setRadioSpinsPage(1);
      setRadioSpinsHasMore(true);
      fetchRadioSpins(uuid, 0, radioSpinsLimit, radioSpinsCountry);
    } else {
      setRadioSpins([]);
      setRadioSpinsError(null);
      setRadioSpinsOffset(0);
      setRadioSpinsPage(1);
    }
  }, [source, uuid, radioSpinsCountry]);


  useEffect(() => {
    fetchProfile();
  }, []);

  // If the profile resolves after fetchInitialData already picked a fallback tab, correct
  // the selection once. This only moves the tab (one playlist fetch for the right DSP) -
  // it never re-runs the pitch/ISRC/popularity requests.
  useEffect(() => {
    if (!preferredAnalyticsDSP || availablePlatforms.length === 0) return;
    const preferredPlatform = dspToPlatformKey[preferredAnalyticsDSP];
    if (preferredPlatform && preferredPlatform !== activePlatform) {
      setActivePlatform(preferredPlatform);
    }
    // `activePlatform` is intentionally not a dependency: this effect reads it only to
    // avoid a redundant set, and including it would re-run on every tab click and snap
    // the user back to the preferred DSP.
  }, [preferredAnalyticsDSP, availablePlatforms]);

  // Single owner of the playlist fetch. `currentOnly` and `selectedCountry` are already
  // dependencies of fetchDataForCurrentTab, so this effect re-runs when either changes -
  // the separate effects that used to watch them fired a duplicate request every toggle.
  useEffect(() => {
    if (activePlatform !== "spotify" && (filterType === "algorithmic" || filterType === "algotorial")) {
      setFilterType("editorial");
      return;
    }
    if (activePlatform && filterType && uuid) {
      fetchDataForCurrentTab();
    }
  }, [activePlatform, filterType, fetchDataForCurrentTab]);

  useEffect(() => {
    fetchInitialData();

    // Nothing writes localStorage["playlists"] any more, but browsers that visited an older
    // build still carry the blob it accumulated. Clearing on unmount evicts it.
    return () => {
      localStorage.removeItem("playlists");
    };
  }, [fetchInitialData]);

  const handleRetry = () => {
    setCurrentPage(1);
    if (activePlatform && filterType && uuid) {
      fetchDataForCurrentTab();
    } else {
      fetchInitialData();
    }
  };

  const displayedPlatforms = user?.Label?.availableAnalyticsDSPs?.length
    ? user.Label.availableAnalyticsDSPs.flatMap((dsp: string) => {
        const platform = dspToPlatformKey[dsp];
        return platform && availablePlatforms.includes(platform) ? [platform] : [];
      })
    : availablePlatforms;

  // Filtering and search logic
  let filteredPlaylists = playlists;
  if (activePlatform) {
    const apiActivePlatform = getApiPlatformCode(activePlatform);
    filteredPlaylists = filteredPlaylists.filter(
      (p) => p.playlist.platform === apiActivePlatform
    );
  }

  if (searchTerm.trim()) {
    filteredPlaylists = filteredPlaylists.filter((p) =>
      p.playlist.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );
  }

  // Deduplication happens on the backend, across the complete result set rather than within a
  // page, so there is nothing to collapse here and no toggle to gate it on.

  // Country filtering now happens API-side via fetchPlaylistsForCurrentTab/goToPlaylistsPage
  // (selectedCountry -> countryCode param), so playlists is already scoped when a country is selected.

  // Sort US playlists first, then playlists with no country code, then everyone else.
  // Uses a stable sort so relative order within each group (e.g. entryDate/position) is preserved.
  const countryPriority = (countryCode?: string) => {
    if (countryCode === "US") return 0;
    if (!countryCode) return 1;
    return 2;
  };
  // In Historical mode the Exit Date column is what the view is about, but SoundCharts
  // returns plenty of entries with no exitDate (still-current placements) interleaved
  // among them. Rank rows that actually exited first, most recent exit at the top, so the
  // meaningful history isn't buried behind "—" rows. Current mode has no Exit Date column,
  // so it keeps the plain country ordering.
  const hasExited = (p: Playlist) => (p.exitDate ? 0 : 1);
  const exitTime = (p: Playlist) => {
    const parsed = p.exitDate ? new Date(p.exitDate).getTime() : NaN;
    return isNaN(parsed) ? null : parsed;
  };
  filteredPlaylists = [...filteredPlaylists].sort((a, b) => {
    // Stale placements stay together at the bottom, under their divider, whatever else
    // the ordering below would do with them.
    const staleDiff = staleLast(a, b);
    if (staleDiff !== 0) return staleDiff;

    if (currentOnly === 0) {
      const exitedDiff = hasExited(a) - hasExited(b);
      if (exitedDiff !== 0) return exitedDiff;

      // Both exited: newest exit first. Unparseable dates sink below valid ones rather
      // than poisoning the comparison with NaN.
      const aTime = exitTime(a);
      const bTime = exitTime(b);
      if (aTime !== null && bTime !== null && aTime !== bTime) return bTime - aTime;
      if (aTime === null && bTime !== null) return 1;
      if (aTime !== null && bTime === null) return -1;
    }
    return countryPriority(a.playlist.countryCode) - countryPriority(b.playlist.countryCode);
  });

  // `playlists` already holds exactly one SoundCharts page (see fetchPlaylistsForCurrentTab /
  // goToPlaylistsPage), so filteredPlaylists IS the page to render - no client-side re-slicing.
  const currentPlaylists = filteredPlaylists;
  const totalPages = Math.max(1, Math.ceil(playlistsPageTotal / itemsPerPage));
  const displayedTotalItems = playlistsPageTotal;

  const countryOptions = Object.entries(countryCodes).map(([code, name]) => ({
    value: code,
    label: name,
  }));

  // Update the handleTrendClick function
  const handleTrendClick = (type: 'artist' | 'song') => {
    if (type === 'artist') {
      setModalTitle('Artist Popularity History');
      setTrendDataType('artist');
    } else {
      setModalTitle('Song Popularity History');
      setTrendDataType('song');
    }
    setShowTrendModal(true);
  };

  const radioSpinColumns: DataTableColumn<any>[] = [
    {
      key: "station",
      header: "Radio Station",
      cell: (spin) => (
        <span className="font-medium">{spin.radio?.name || "Unknown Station"}</span>
      ),
    },
    {
      key: "city",
      header: "City",
      cell: (spin) => spin.radio?.cityName || "—",
    },
    {
      key: "date",
      header: "Date",
      cell: (spin) => (spin.airedAt ? formatDisplayDate(spin.airedAt) : "—"),
    },
  ];

  /*
   * Playlist table columns. Which columns appear still depends on the same two
   * pieces of state as before - Follower Count is Spotify-only, and the
   * Current Position / Exit Date pair swaps with the Current/Historical toggle.
   */
  const playlistColumns: DataTableColumn<Playlist>[] = [
    {
      key: "playlist",
      header: "Playlist",
      cell: (item) => (
        <div className="flex items-center gap-3">
          <img
            src={item.playlist.imageUrl}
            alt={item.playlist.name}
            className={`object-cover w-10 h-10 rounded-md shadow-sm shrink-0 bg-neutral-200 ${item.isStale ? staleArtworkClass : ""}`}
            onError={(e) =>
            ((
              e.target as HTMLImageElement
            ).style.display = "none")
            }
          />
          <span className="max-w-xs truncate" title={item.playlist.name}>
            {item.playlist.name}
          </span>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      cell: (item) => (
        // A category label, not a status - all three variants share one
        // neutral badge rather than borrowing a status colour.
        <span className="inline-flex items-center justify-center px-2.5 py-0.5 text-xs font-medium border rounded-full bg-neutral-100 text-neutral-700 border-neutral-200">
          {item.playlistType === "editorial"
            ? "Editorial"
            : item.playlistType === "algorithmic"
              ? "Algorithmic"
              : "Algotorial"}
        </span>
      ),
    },
    ...(activePlatform === "spotify"
      ? [
          {
            key: "followers",
            header: "Follower Count",
            cell: (item: Playlist) =>
              item.playlist.latestSubscriberCount != null
                ? item.playlist.latestSubscriberCount.toLocaleString()
                : "—",
          },
        ]
      : []),
    {
      key: "peak",
      header: "Peak Position",
      cell: (item) => item.peakPosition,
    },
    ...(currentOnly === 1
      ? [
          {
            key: "current",
            header: "Current Position",
            cell: (item: Playlist) => (
              <CurrentPositionCell
                item={item}
                formatDate={formatDisplayDate}
                staleAfterDays={playlistsMeta?.staleAfterDays}
              />
            ),
          },
        ]
      : []),
    ...(currentOnly === 0
      ? [
          {
            key: "exit",
            header: "Exit Date",
            cell: (item: Playlist) => (
              <ExitDateCell item={item} formatDate={formatDisplayDate} />
            ),
          },
        ]
      : []),
    {
      key: "entry",
      header: "Entry Date",
      cell: (item) => <EntryDateCell item={item} formatDate={formatDisplayDate} />,
    },
  ];

  return (
    <div className="py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[
          HOME_CRUMB,
          { label: source === "analytics" ? "Analytics" : "Past Submissions", to: backTo },
          { label: "Track Analytics" },
        ]}
        className="mb-6"
        title={`Analytics for ${pitch?.releaseTitle || "Track"}`}
      />

      {initialLoading ? (
        /*
          Same treatment as the Review page: placeholders shaped like the real
          sections — track details, the stat row, then the playlist table — so
          the page holds its layout while the pitch and SoundCharts lookups
          resolve, instead of collapsing to a single pulsing dot.
        */
        <div aria-busy="true">
          <div className="p-4 mb-6 bg-white border rounded-lg border-neutral-200 sm:p-6">
            <Skeleton className="w-40 h-5 mb-4" />
            <SkeletonFields count={6} />
          </div>

          <Skeleton className="w-48 h-5 mb-4" />
          <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-3.5 py-3 bg-white border rounded-xl border-neutral-200"
              >
                <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                  <Skeleton className="w-20 h-3" />
                  <Skeleton className="w-12 h-5" />
                </div>
              </div>
            ))}
          </div>

          <Skeleton className="w-32 h-5 mb-3" />
          <div className="overflow-hidden bg-white border rounded-lg border-neutral-200">
            <table className="min-w-full">
              <tbody>
                <SkeletonTableRows rows={6} columns={5} />
              </tbody>
            </table>
          </div>
        </div>
      ) : error ? (
        <div className="p-6 border border-danger-200 rounded-lg bg-danger-50">
          <div className="flex items-start space-x-3">
            <svg
              className="w-5 h-5 text-danger-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h3 className="text-sm font-bold text-danger-800">
                Error Loading Analytics
              </h3>
              <p className="mt-1 text-sm text-danger-700">{error}</p>
              <Button variant="secondary" onClick={handleRetry} className="mt-4">
                Try Again
              </Button>
            </div>
          </div>
        </div>
      ) : !pitch ? (
        <div className="p-6 text-center border rounded-lg bg-neutral-50 border-neutral-100">
          <p className="text-neutral-600">
            No pitch data found for the provided ID.
          </p>
        </div>
      ) : (
        <>

          <div className="mb-8">
            <h2 className="mb-4 text-base font-semibold text-black">
              Track Details
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* Song Popularity Score */}
              <div>
                <dt className="flex items-center gap-2 text-xs font-medium tracking-wide uppercase text-neutral-500">
                  <span>Song Popularity Score</span>
                  <button
                    onClick={() => handleTrendClick('song')}
                    className="border border-transparent p-1.5 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer focus:outline-none focus-visible:border-primary-700"
                    title="Click to view historical trend"
                  >
                    <BarChart3 size={18} />
                  </button>
                </dt>
                <dd className="mt-1 text-sm text-black break-words">
                  {popularityLoading ? (
                    <Skeleton className="w-10 h-4" />
                  ) : songPopularity !== null ? songPopularity : "N/A"}
                </dd>
              </div>

              {/* Artist Popularity Score */}
              <div>
                <dt className="flex items-center gap-2 text-xs font-medium tracking-wide uppercase text-neutral-500">
                  <span>Artist Popularity Score</span>
                  <button
                    onClick={() => handleTrendClick('artist')}
                    className="border border-transparent p-1.5 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer focus:outline-none focus-visible:border-primary-700"
                    title="Click to view historical trend"
                  >
                    <BarChart3 size={18} />
                  </button>
                </dt>
                <dd className="mt-1 text-sm text-black break-words">
                  {popularityLoading ? (
                    <Skeleton className="w-10 h-4" />
                  ) : artistPopularity !== null ? artistPopularity : "N/A"}
                </dd>
              </div>

              {pitch.artistName && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">Artist Name</dt>
                  <dd className="mt-1 text-sm text-black break-words">{pitch.artistName}</dd>
                </div>
              )}
              {pitch.releaseTitle && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">
                    Release Title
                  </dt>
                  <dd className="mt-1 text-sm text-black break-words">{pitch.releaseTitle}</dd>
                </div>
              )}
              {pitch.releaseDate && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">
                    Release Date
                  </dt>
                  <dd className="mt-1 text-sm text-black break-words">
                    {formatDisplayDate(pitch.releaseDate)}
                  </dd>

                </div>
              )}
              {pitch.configuration && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">
                    Configuration
                  </dt>
                  <dd className="mt-1 text-sm text-black break-words">{pitch.configuration}</dd>
                </div>
              )}
              {pitch.releaseUPC && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">UPC</dt>
                  <dd className="mt-1 text-sm text-black break-words">{pitch.releaseUPC}</dd>
                </div>
              )}
              {pitch.trackISRC && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">ISRC</dt>
                  <dd className="mt-1 text-sm text-black break-words">{pitch.trackISRC}</dd>
                </div>
              )}
              {pitch.featuredArtists && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">
                    Featured Artists
                  </dt>
                  <dd className="mt-1 text-sm text-black break-words">
                    {pitch.featuredArtists || "—"}
                  </dd>
                </div>
              )}
              {pitch.mainGenre && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">Main Genre</dt>
                  <dd className="mt-1 text-sm text-black break-words">{pitch.mainGenre}</dd>
                </div>
              )}
              {pitch.songMood && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">Song Mood</dt>
                  <dd className="mt-1 text-sm text-black break-words">{pitch.songMood}</dd>
                </div>
              )}
              {pitch.language && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">Language</dt>
                  <dd className="mt-1 text-sm text-black break-words">{pitch.language}</dd>
                </div>
              )}
              {pitch.linkToListen && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">
                    Link to Listen
                  </dt>
                  <a
                    href={pitch.linkToListen}
                    className="block break-all text-primary-700 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {pitch.linkToListen}
                  </a>
                </div>
              )}
              {pitch.promoPhotos && (
                <div>
                  <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">
                    Promo Photos
                  </dt>
                  <a
                    href={pitch.promoPhotos}
                    className="block break-all text-primary-700 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {pitch.promoPhotos}
                  </a>
                </div>
              )}
            </div>
          </div>

          {!pitch.trackISRC ? (
            <div className="p-4 border rounded-lg border-warning-200 bg-warning-50">
              <p className="text-warning-800">
                No ISRC found. Playlist analytics unavailable.
              </p>
            </div>
          ) : trackLookupNotice ? (
            <div className="p-4 border rounded-lg border-warning-200 bg-warning-50">
              <div className="flex items-start space-x-3">
                <svg
                  className="w-5 h-5 mt-0.5 text-warning-500 shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <h3 className="text-sm font-bold text-warning-800">
                    Streaming analytics unavailable
                  </h3>
                  <p className="mt-1 text-sm text-warning-800">
                    {trackLookupNotice}
                  </p>
                  {/* Carries `page` onward so Edit -> back -> back still lands
                      on the list page the user originally came from. */}
                  <Link
                    to={`/pitch/edit/${pitch.id}?from=analytics&source=${source}${
                      returnPage ? `&page=${encodeURIComponent(returnPage)}` : ""
                    }`}
                    className={`${formSecondaryButtonClass} mt-4`}
                  >
                    Edit Pitch
                  </Link>
                </div>
              </div>
            </div>
          ) : availablePlatforms.length === 0 ? (
            <div className="p-6 text-center border rounded-lg bg-neutral-50 border-neutral-100">
              <p className="text-neutral-600">
                No supported platforms found for this pitch.
              </p>
            </div>
          ) : (

            <> {isReleaseDateOlderThan30Days && (<div className="mb-8">
              <h2 className="mb-4 text-base font-semibold text-black">
                First Month Streams
              </h2>
              <div className="p-6 bg-white border rounded-lg border-neutral-200">
                {audienceLoading ? (
                  <div className="py-2" aria-busy="true">
                    <Skeleton className="w-40 h-10 mx-auto mb-2" />
                    <Skeleton className="w-24 h-3 mx-auto mb-8" />
                    <SkeletonText lines={4} />
                  </div>
                ) : (
                  <>
                    {/* Total Audience */}
                    <div className="flex flex-col items-center justify-center mb-6">
                      <span className="text-4xl font-extrabold text-primary-700">
                        {totalAudience == null ? "No data" : totalAudience.toLocaleString()}
                      </span>
                      {totalAudience != null && (
                        <span className="mt-1 text-sm font-medium text-neutral-500">streams</span>
                      )}
                    </div>
                    <div className="my-4 border-t border-neutral-100"></div>
                    {/* Platform Breakdown */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      {["spotify", "youtube", "soundcloud", "audiomack"].map((platform) => (
                        <StatCard
                          key={platform}
                          label={
                            platform === "youtube" ? (
                              <span className="inline-flex items-center gap-1">
                                {platformDisplayNames[platform] || platform}
                                <span
                                  tabIndex={0}
                                  title={YOUTUBE_AUDIENCE_TOOLTIP}
                                  aria-label={YOUTUBE_AUDIENCE_TOOLTIP}
                                  className="inline-flex cursor-help"
                                >
                                  <InformationCircleIcon className="w-3.5 h-3.5" />
                                </span>
                              </span>
                            ) : (
                              platformDisplayNames[platform] || platform
                            )
                          }
                          value={
                            audienceByPlatform[platform] == null
                              ? "No data"
                              : audienceByPlatform[platform]!.toLocaleString()
                          }
                        />
                      ))}
                    </div>
                    <div className="mt-4 text-xs text-center text-neutral-500">
                      Audience numbers are aggregated for each platform during the first 30 days after release.
                    </div>
                  </>
                )}
              </div>
            </div>)
            }

              {/* Radio Spins Table  */}
              {source === "analytics" && (
                <div className="mb-8">
                  <div className="flex items-center justify-between">
                    <h2 className="mb-4 text-base font-semibold text-black">
                      Radio Spins
                    </h2>
                    <button
                      onClick={() => setShowRadioSpinsExportModal(true)}
                      className={primaryButtonClass}
                    >
                      <ArrowDownTrayIcon className="w-5 h-5" />
                      Export
                    </button>
                  </div>
                  {/* Country filter buttons */}
                  <div className="flex gap-2 mb-4">
                    {[
                      { code: "US", label: "United States" },
                      { code: "GB", label: "United Kingdom" },
                    ].map((country) => (
                      <button
                        key={country.code}
                        onClick={() => setRadioSpinsCountry(country.code)}
                        className={pillClass(radioSpinsCountry === country.code)}
                      >
                        {country.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-6 bg-white border rounded-lg border-neutral-200">
                    {radioSpinsLoading ? (
                      <div className="py-2" aria-busy="true">
                        <Skeleton className="w-40 h-10 mx-auto mb-2" />
                        <Skeleton className="w-24 h-3 mx-auto mb-8" />
                        <SkeletonText lines={4} />
                      </div>
                    ) : radioSpinsError ? (
                      <div className="p-4 text-center text-danger-600 rounded-lg bg-danger-50">
                        {radioSpinsError}
                      </div>
                    ) : radioSpins.length === 0 ? (
                      <div className="p-4 text-center text-neutral-600">
                        No radio spins data available for this track.
                      </div>
                    ) : (
                      <div>
                        <DataTable
                          columns={radioSpinColumns}
                          rows={radioSpins}
                          getRowKey={(spin: any, index: number) =>
                            `${spin.radio?.slug || index}-${spin.airedAt || index}`
                          }
                          emptyMessage="No radio spins data available for this track."
                        />

                        {/* Pagination Controls */}
                        <div className="mt-4">
                          <Pagination
                            page={radioSpinsPage}
                            totalPages={Math.ceil(radioSpinsTotal / radioSpinsLimit) || 1}
                            setPage={goToRadioSpinsPage}
                            totalItems={radioSpinsTotal}
                            limit={radioSpinsLimit}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}




              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <h2 className="mb-4 text-base font-semibold text-black">
                    Playlist Analytics by Platform
                  </h2>
                  <button
                    onClick={() => setShowExportModal(true)}
                    className={primaryButtonClass}
                  >
                    <ArrowDownTrayIcon className="w-5 h-5" />
                    Export
                  </button>

                </div>
                <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Search playlist names"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {Object.keys(platformDisplayNames).map((platform) => (
                    <button
                      key={platform}
                      onClick={() => setActivePlatform(platform)}
                      className={pillClass(activePlatform === platform)}
                    >
                      {platformDisplayNames[platform] || platform}
                    </button>
                  ))}
                </div>

                <div className="flex mb-4 space-x-4">
                  {(activePlatform === "spotify"
                    ? ["editorial", "algorithmic", "algotorial"]
                    : ["editorial"]
                  ).map((type) => {
                    const displayLabel =
                      type === "editorial" && activePlatform === "sound-cloud"
                        ? "Curators & Listeners"
                        : type.charAt(0).toUpperCase() + type.slice(1);
                    return (
                      <label
                        key={type}
                        className="flex items-center py-2 space-x-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          value={type}
                          checked={filterType === type}
                          onChange={() =>
                            setFilterType(
                              type as "editorial" | "algorithmic" | "algotorial"
                            )
                          }
                          className="w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
                        />
                        <span
                          className={`text-sm ${filterType === type
                            ? "text-primary-700 font-semibold"
                            : "text-neutral-700"
                            }`}
                        >
                          {displayLabel}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-neutral-700">
                  Filter by Country
                </label>
                <Select
                  isClearable
                  options={countryOptions}
                  value={selectedCountry}
                  onChange={(selected: any) => setSelectedCountry(selected)}
                  className="react-select-container z-30"
                  classNamePrefix="react-select"
                  placeholder="Select a country..."
                />
              </div>

              <div className="mb-8">

                <div className="flex gap-4 mb-4">
                  <button
                    onClick={() => setCurrentOnly(1)}
                    className={pillClass(currentOnly === 1)}
                  >
                    Current Data
                  </button>
                  <button
                    onClick={() => setCurrentOnly(0)}
                    className={pillClass(currentOnly === 0)}
                  >
                    Historical Data
                  </button>
                </div>
                <div className="flex items-center justify-between w-full mb-4">
                  <h3 className="text-base font-semibold text-black">
                    {currentOnly === 1 ? "Current" : "Historical"}{" "}
                    {filterType === "editorial" && activePlatform === "sound-cloud"
                      ? "Curators & Listeners"
                      : filterType.charAt(0).toUpperCase() + filterType.slice(1)}{" "}
                    Playlists on {platformDisplayNames[activePlatform]}
                  </h3>
                  <CSVDownload activePlatform={activePlatform} rows={playlists} />
                </div>

                {/* A count of real placements, deduplicated on the backend across the complete
                    result set, with when SoundCharts collected them and a way to fetch newer. */}
                {loading ? (
                  <Skeleton className="w-32 h-4 mb-2" />
                ) : (
                  <PlaylistFreshness
                    countLabel={playlistCountLabel(
                      playlistsTotal,
                      playlistsCapped,
                      playlistsCountry,
                      playlistsMeta?.staleCount,
                      currentOnly
                    )}
                    meta={playlistsMeta}
                    onRefresh={handleRefreshPlaylists}
                    refreshing={refreshingPlaylists}
                    refreshMessage={refreshMessage}
                  />
                )}

                {/* No fixed height: a page is only 10 rows, so a second scrollbar inside
                    the page scroll just hid the last rows on large screens. */}
                <DataTable
                  columns={playlistColumns}
                  rows={currentPlaylists}
                  // Every fetch (platform, type, country, page) — not only the first —
                  // so the previous selection's rows never sit under the new heading.
                  loading={loading}
                  getRowKey={(item: Playlist, index: number) =>
                    `${item.playlist.uuid}-${item.playlistType}-${index}`
                  }
                  isRowMuted={(item: Playlist) => Boolean(item.isStale)}
                  renderBeforeRow={(_item: Playlist, index: number, colSpan: number) =>
                    startsStaleGroup(currentPlaylists, index) ? (
                      <StaleDividerRow colSpan={colSpan} count={playlistsMeta?.staleCount ?? 0} />
                    ) : null
                  }
                  emptyMessage={
                    // Before the first sync there is nothing to say about placements either way.
                    playlistsMeta?.syncStatus === "never_synced"
                      ? "No playlist data collected yet."
                      : "No results found for your search and filter criteria."
                  }
                  head={
                    <thead>
                      <tr className={dataTableTheadRowClass}>
                        {playlistColumns.map((col) => (
                          <th key={col.key} scope="col" className={dataTableThClass}>
                            {col.header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  }
                />

                <div className="mt-3">
                  <Pagination
                    page={currentPage}
                    totalPages={totalPages}
                    setPage={goToPlaylistsPage}
                    totalItems={displayedTotalItems}
                    limit={itemsPerPage}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}

      <AnalyticsExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        availablePlatforms={Object.keys(platformDisplayNames)}
        platformDisplayNames={platformDisplayNames}
        uuid={uuid}
        defaultPlatform={activePlatform}
        defaultType={filterType}
        defaultDataScope={currentOnly}
        artistName={pitch?.artistName} // <-- pass here
        releaseTitle={pitch?.releaseTitle}
        selectedCountries={selectedCountry ? [selectedCountry.value] : []} // <-- Pass selected country here
      />

      <RadioSpinsExportModal
        isOpen={showRadioSpinsExportModal}
        onClose={() => setShowRadioSpinsExportModal(false)}
        uuid={uuid}
        defaultCountry={radioSpinsCountry}
        artistName={pitch?.artistName}
        releaseTitle={pitch?.releaseTitle}
      />

      <HistoricalTrendModal
        isOpen={showTrendModal}
        onClose={() => setShowTrendModal(false)}
        title={modalTitle}
        dataType={trendDataType}
        songUuid={uuid}
        artistUuid={artistUuid}
      />
    </div>
  );
};

export default Analytics;